// Kasse ohne Verbindung – gemeinsame Begriffe, Startpaket und Stammdaten-Paket (docs/kasse-offline/SPEZIFIKATION.md
// §5, §9.4, §10, §14.1). Rein, für Server, Gerät und Oberfläche; die Verträge (contract/offline.ts) bauen darauf auf.
//
// Pakete sind Daten für das Gerät, keine Ansichten: Zeitpunkte stehen als ISO-Text (UTC oder mit Versatz) darin, nicht
// als `z.date()`. So ist der Prüfwert (SHA-256 über die kanonische Form) auf Server und Gerät byte-gleich, und
// IndexedDB speichert nur JSON. Geld in Cent, Mengen in Tausendstel, Sätze und Rabatte in Basispunkten.
import type { ComposeData } from '@shared/documentCompose'
import { zId } from '@shared/ids'
import { z } from 'zod'
import { zFiscalMode } from '../../contract/views'
import { OFFLINE_LIMIT_RANGES, SNAPSHOT_FORMAT, SNAPSHOT_MAX_UNITS, STARTPACK_FORMAT, type OfflineLimits } from './limits'

// ---- Bausteine -----------------------------------------------------------------------------------------------------

/** 64 Hexzeichen in Kleinbuchstaben: SHA-256 (Prüfwert eines Satzes, Kettenwert, Paket-Version, Fingerabdruck). */
export const zHex64 = z.string().regex(/^[0-9a-f]{64}$/, { error: 'Der Prüfwert hat nicht die erwartete Form.' })
/** Zeitpunkt als ISO-Text mit Zone (Z oder ±hh:mm), z. B. 2026-10-20T10:42:17.120+02:00 */
export const zIsoDateTime = z.iso.datetime({ offset: true, error: 'Bitte einen gültigen Zeitpunkt angeben.' })
/** Kalendertag 'YYYY-MM-DD' (Europe/Berlin) */
export const zIsoDay = z.iso.date({ error: 'Bitte ein gültiges Datum eingeben.' })
/** ECDSA-Signatur r‖s (64 Byte) in Base64 */
export const zSignature = z.string().regex(/^[A-Za-z0-9+/]{86}==$/, { error: 'Die Signatur hat nicht die erwartete Form.' })
/** öffentlicher Schlüssel (SPKI, DER) in Base64 */
export const zPublicKeySpki = z
  .string()
  .max(400, { error: 'Der öffentliche Schlüssel ist zu lang.' })
  .regex(/^[A-Za-z0-9+/]+={0,2}$/, { error: 'Der öffentliche Schlüssel hat nicht die erwartete Form.' })
/** Server-Nonce für Geräte-Nachweise (Base64url oder Hex) */
export const zNonce = z.string().min(16).max(64).regex(/^[A-Za-z0-9_-]+$/)

// ---- Begriffe ------------------------------------------------------------------------------------------------------

/** Zustand einer Kopplung (pos_till_device.state) */
export const DEVICE_STATES = ['active', 'blocked', 'retired', 'lost'] as const
export type DeviceState = (typeof DEVICE_STATES)[number]
export const zDeviceState = z.enum(DEVICE_STATES)

/** Art des Endes einer Kopplung (pos_till_device.end_kind) */
export const DEVICE_END_KINDS = ['orderly', 'defective', 'lost'] as const
export type DeviceEndKind = (typeof DEVICE_END_KINDS)[number]

/** Plattform und Darstellungsart, vom Gerät gemeldet */
export const DEVICE_PLATFORMS = ['browser', 'webapp', 'ios', 'android'] as const
export type DevicePlatform = (typeof DEVICE_PLATFORMS)[number]
export const DISPLAY_MODES = ['standalone', 'browser'] as const
export type DisplayMode = (typeof DISPLAY_MODES)[number]

/** Zustandsautomat der Verbindung auf dem Gerät (§10.1) */
export const CONNECTION_STATES = ['online', 'pruefen', 'stoerung', 'offline', 'rueckkehr', 'gesperrt'] as const
export type ConnectionState = (typeof CONNECTION_STATES)[number]
export const zConnectionState = z.enum(CONNECTION_STATES)

/** Einordnung einer Antwort durch den Transport (§10.1, web/api/client.ts – AP-P) */
export const REACH_REASONS = ['network', 'timeout', 'foreign', 'edge', 'server', 'ok'] as const
export type ReachReason = (typeof REACH_REASONS)[number]
/** Diese Einordnungen zählen als „keine Antwort“ (nur sie führen nach der Karenz nach offline) */
export const NO_ANSWER_REASONS: readonly ReachReason[] = ['network', 'timeout', 'foreign']

/** Lage eines Ausfalls (§8.3, fiscal_outage.lage) */
export const OUTAGE_LAGEN = ['internet', 'server', 'unknown'] as const
export type OutageLage = (typeof OUTAGE_LAGEN)[number]
export const zOutageLage = z.enum(OUTAGE_LAGEN)

/** Art eines Eintrags im Ausfallprotokoll (fiscal_outage.kind) */
export const OUTAGE_KINDS = ['unit', 'offline'] as const
export type OutageKind = (typeof OUTAGE_KINDS)[number]

/** Ursache eines Ausfalls (§8.6, pos_outage_note.cause) */
export const OUTAGE_CAUSES = ['provider', 'router', 'power', 'merxo_server', 'device', 'other', 'unknown'] as const
export type OutageCause = (typeof OUTAGE_CAUSES)[number]
export const zOutageCause = z.enum(OUTAGE_CAUSES)

/** Grund einer Nummernreservierung (pos_offline_reservation.reason) */
export const RESERVATION_REASONS = ['reconnect', 'lost'] as const
export type ReservationReason = (typeof RESERVATION_REASONS)[number]

/** Zweck einer Reihe von Vordrucken (pos_paper_form_range.purpose) */
export const PAPER_PURPOSES = ['print', 'device'] as const
export type PaperPurpose = (typeof PAPER_PURPOSES)[number]

/** Herkunft einer Übertragung (pos_offline_entry.source) */
export const ENTRY_SOURCES = ['device', 'file'] as const
export type EntrySource = (typeof ENTRY_SOURCES)[number]

/** Ergebnis je übertragenem Satz in der Antwort von pos.offline.submit; gespeichert werden nur accepted und quarantined */
export const SUBMIT_RESULTS = ['accepted', 'duplicate', 'quarantined'] as const
export type SubmitResult = (typeof SUBMIT_RESULTS)[number]
export const STORED_ENTRY_RESULTS = ['accepted', 'quarantined'] as const

/** Warum die Verarbeitung eines Stapels anhielt (pos.offline.submit → stoppedAt.reason) */
export const SUBMIT_STOP_REASONS = ['seq', 'device_blocked', 'quarantine'] as const
export type SubmitStopReason = (typeof SUBMIT_STOP_REASONS)[number]

/** Zustand eines Satzes auf dem Gerät (IndexedDB entries.state, §9.1) */
export const DEVICE_ENTRY_STATES = ['pending', 'accepted', 'quarantined'] as const
export type DeviceEntryState = (typeof DEVICE_ENTRY_STATES)[number]

// ---- Klärfälle (§10.5, pos_offline_case) ---------------------------------------------------------------------------

export const CASE_KINDS = [
  'price_hint',
  'operator_hint',
  'rights_hint',
  'clock_hint',
  'duplicate_online',
  'stock_late',
  'negative_stock',
  'outage_doc',
  'record_mismatch',
  'booking_pending',
  'tse_conflict',
  'quarantine',
  'gap',
  'paper_pending',
  'lost_device_data',
] as const
export type CaseKind = (typeof CASE_KINDS)[number]
export const zCaseKind = z.enum(CASE_KINDS)

export const CASE_RESOLUTIONS = ['acknowledge', 'retry', 'book_in_open_period', 'take_over', 'confirm_gap', 'confirm_unused', 'documented'] as const
export type CaseResolution = (typeof CASE_RESOLUTIONS)[number]
export const zCaseResolution = z.enum(CASE_RESOLUTIONS)

export interface CaseRule {
  /** sperrt den Kassenabschluss: ja, nein oder nach der Einstellung outage_doc_blocking */
  blocking: boolean | 'setting'
  /** Erledigungen über pos.offline.resolve */
  resolutions: readonly CaseResolution[]
  /** Erledigungen, bei denen eine Begründung bzw. Notiz Pflicht ist (mindestens RESOLUTION_NOTE_MIN Zeichen) */
  noteRequired: readonly CaseResolution[]
  /** erledigt eine andere Aktion von selbst (outage_doc: pos.offline.outage.document; paper_pending: Blatt erfassen) */
  resolvedBy?: 'pos.offline.outage.document' | 'pos.paper.sheet.record'
}

/** Regeln je Art – dieselbe Tabelle für Server (Prüfung), Verwaltung (Knöpfe) und Abschlusssperre. */
export const CASE_RULES: Readonly<Record<CaseKind, CaseRule>> = Object.freeze({
  price_hint: { blocking: false, resolutions: ['acknowledge'], noteRequired: [] },
  operator_hint: { blocking: false, resolutions: ['acknowledge'], noteRequired: [] },
  rights_hint: { blocking: false, resolutions: ['acknowledge'], noteRequired: ['acknowledge'] },
  clock_hint: { blocking: false, resolutions: ['acknowledge'], noteRequired: [] },
  duplicate_online: { blocking: false, resolutions: ['acknowledge'], noteRequired: [] },
  stock_late: { blocking: false, resolutions: ['acknowledge'], noteRequired: [] },
  negative_stock: { blocking: false, resolutions: ['acknowledge'], noteRequired: [] },
  outage_doc: { blocking: 'setting', resolutions: [], noteRequired: [], resolvedBy: 'pos.offline.outage.document' },
  record_mismatch: { blocking: true, resolutions: ['acknowledge'], noteRequired: ['acknowledge'] },
  booking_pending: { blocking: true, resolutions: ['retry', 'book_in_open_period'], noteRequired: [] },
  tse_conflict: { blocking: true, resolutions: ['acknowledge'], noteRequired: ['acknowledge'] },
  quarantine: { blocking: true, resolutions: ['take_over', 'confirm_gap'], noteRequired: ['confirm_gap'] },
  gap: { blocking: true, resolutions: ['confirm_gap'], noteRequired: ['confirm_gap'] },
  paper_pending: { blocking: true, resolutions: ['confirm_unused'], noteRequired: ['confirm_unused'], resolvedBy: 'pos.paper.sheet.record' },
  lost_device_data: { blocking: false, resolutions: ['acknowledge'], noteRequired: [] },
})

/** Sperrt ein Klärfall dieser Art den Abschluss? (outage_doc nach der Einstellung des Betriebs) */
export const caseBlocks = (kind: CaseKind, outageDocBlocking: boolean): boolean => {
  const blocking = CASE_RULES[kind].blocking
  return blocking === 'setting' ? outageDocBlocking : blocking
}

// ---- Prüfungen beim Übertragen (§9.4, pos_offline_entry.checks) ----------------------------------------------------

/**
 * Schlüssel des Prüfprotokolls je Satz. `checks` ist ein Objekt { [Schlüssel]: EntryCheck } – nur die Prüfungen, die
 * gelaufen sind; die erste gescheiterte, die den Kettenstand anhält, ist die letzte im Objekt.
 */
export const ENTRY_CHECK_KEYS = [
  'proof', // Stufe A: Stapel-Nachweis (source device) bzw. Recht (source file)
  'device', // Stufe A: Gerät bekannt, gehört zu Betrieb und Kasse, Zustand
  'size', // Größe in UTF-8-Bytes
  'json', // lesbar
  'format', // formatVersion unterstützt
  'schema', // Schema der Fassung
  'hash', // hash = SHA-256(payload)
  'signature', // Signatur des Geräteschlüssels
  'binding', // Mandant, Kasse, Gerät im Satz = Aufruf
  'sequence', // deviceSeq = confirmed_seq + 1
  'chain', // prevHash = confirmed_hash
  'bonId', // Vorgangsnummer nach §7.4
  'shift', // Schicht offen
  'record', // Aufzeichnung in sich stimmig, Zeilenbildung, Grenzen
  'clock', // Uhr plausibel
  'rights', // Rechte des gespeicherten Startpakets
  'operator', // Bediener heute noch berechtigt
  'prices', // angewandte Werte = Stammdatenstand
  'takeover', // Übernahme (Stufe 1–5)
] as const
export type EntryCheckKey = (typeof ENTRY_CHECK_KEYS)[number]

export const zEntryCheck = z.object({
  ok: z.boolean(),
  /** kurzer deutscher Satz zum Ergebnis (bei Fehlern Pflicht) */
  text: z.string().max(300).nullable(),
  /** zusätzliche Werte (erwartete Folgenummer, Stufe der Übernahme …) */
  detail: z.record(z.string(), z.unknown()).optional(),
})
export type EntryCheck = z.output<typeof zEntryCheck>
export const zEntryChecks = z.partialRecord(z.enum(ENTRY_CHECK_KEYS), zEntryCheck)
export type EntryChecks = z.output<typeof zEntryChecks>

/** Stufe der Übernahme eines Verkaufs (§10.4) */
export const TAKEOVER_STAGES = ['as_printed', 'record_amounts', 'condensed', 'record_only', 'raw'] as const
export type TakeoverStage = (typeof TAKEOVER_STAGES)[number]

// ---- Grenzen je Betrieb (§11.1) ------------------------------------------------------------------------------------

const zLimitInt = (min: number, max?: number) => (max === undefined ? z.int().min(min) : z.int().min(min).max(max))

/** Grenzen des Betriebs, wie Startpaket, Einstellungen und Kasse sie lesen (pos_offline_setting) */
export const zOfflineLimits = z.object({
  maxOfflineMinutes: zLimitInt(OFFLINE_LIMIT_RANGES.maxOfflineMinutes.min, OFFLINE_LIMIT_RANGES.maxOfflineMinutes.max),
  maxVorgaenge: zLimitInt(OFFLINE_LIMIT_RANGES.maxVorgaenge.min, OFFLINE_LIMIT_RANGES.maxVorgaenge.max),
  maxBonCents: zLimitInt(OFFLINE_LIMIT_RANGES.maxBonCents.min, OFFLINE_LIMIT_RANGES.maxBonCents.max),
  maxEmptiesCents: zLimitInt(OFFLINE_LIMIT_RANGES.maxEmptiesCents.min),
  maxPayoutCents: zLimitInt(OFFLINE_LIMIT_RANGES.maxPayoutCents.min),
  maxCashInCents: zLimitInt(OFFLINE_LIMIT_RANGES.maxCashInCents.min, OFFLINE_LIMIT_RANGES.maxCashInCents.max),
  warnAfterMinutes: zLimitInt(OFFLINE_LIMIT_RANGES.warnAfterMinutes.min),
  cardReferenceRequired: z.boolean(),
  outageDocBlocking: z.boolean(),
}) satisfies z.ZodType<OfflineLimits>

// ---- Startpaket (§5.2) ---------------------------------------------------------------------------------------------

/**
 * Rechte-Teil: ändert sich selten. Sein Prüfwert `startpackHash` = hex(SHA-256(canonicalJson(rights))) steht in jedem
 * Offline-Satz; der Server legt jeden ausgegebenen Rechte-Teil einmal je Gerät und Prüfwert in pos_offline_startpack ab
 * und prüft beim Übertragen gegen ihn (§5.3). Kein Kennwort, kein Sitzungs-Token, keine PIN-Prüfwerte.
 */
export const zStartpackRights = z.object({
  format: z.literal(STARTPACK_FORMAT),
  tenant: z.object({ id: zId, name: z.string(), demo: z.boolean() }),
  register: z.object({
    id: zId,
    code: z.string(),
    name: z.string(),
    /** Seriennummer der Kasse (MERXO-…) */
    serial: z.string().nullable(),
    locationId: zId,
    /** Kürzel des Standorts (Ersatz-Sitzung der Hülle beim Kaltstart, AP-L-wunsch-03) */
    locationCode: z.string(),
    locationName: z.string(),
    floatCents: z.int(),
    diffLimitCents: z.int(),
    /** Schnelltasten (Name und Preis rechnet das Gerät aus dem Stammdaten-Paket – Preise gehören nicht in den Rechte-Teil) */
    quickKeys: z.array(z.object({ itemId: zId, unitId: zId })).max(24),
  }),
  device: z.object({ id: zId, name: z.string(), state: zDeviceState }),
  /** offene Schicht – null: kein Verkauf ohne Verbindung */
  shift: z.object({ id: zId, businessDate: zIsoDay, openedAt: zIsoDateTime, openedByName: z.string() }).nullable(),
  /** angemeldete Person mit ihren Rechten am Standort der Kasse (wie tillState.operator) */
  operator: z.object({
    id: zId,
    name: z.string(),
    canSell: z.boolean(),
    canVoid: z.boolean(),
    /** Rabattgrenze in Basispunkten; null = unbegrenzt, 0 = kein Rabattrecht */
    maxDiscountBp: z.int().min(0).max(10_000).nullable(),
    canMoveCash: z.boolean(),
    canCount: z.boolean(),
    canReturn: z.boolean(),
  }),
  fiscal: z.object({
    unitId: zId,
    unitSerial: z.string(),
    clientSerial: z.string().nullable(),
    mode: zFiscalMode,
    /** Kennzeichen nach Betriebsart (marksOf): Marke oben/unten auf dem Bon, Hinweis auf dem Bon, Hinweis am Bildschirm */
    mark: z.string().nullable(),
    notice: z.string().nullable(),
    banner: z.string().nullable(),
  }),
  /** Firmendaten der Schicht (pos_shift.opening_snapshot.seller) – stehen auf dem Offline-Bon */
  seller: z.object({ name: z.string(), lines: z.array(z.string()), taxId: z.string().nullable() }),
  settings: zOfflineLimits,
  /** false u. a. ohne Freigabe, im Echtbetrieb solange OFFLINE_LIVE_ALLOWED = false, bei gesperrtem Gerät, ohne Schicht */
  offline: z.object({ allowed: z.boolean(), reason: z.string().nullable() }),
})
export type StartpackRights = z.output<typeof zStartpackRights>

/** Stand-Teil: ändert sich mit jedem Herzschlag (gehört nicht in den Prüfwert). */
export const zStartpackState = z.object({
  issuedAt: zIsoDateTime,
  /** issuedAt + max_offline_minutes */
  validUntil: zIsoDateTime,
  /** Prüfwert des Rechte-Teils, wie ihn der Server berechnet und gespeichert hat – das Gerät übernimmt ihn in jeden Satz */
  rightsHash: zHex64,
  /** höchste vergebene bzw. reservierte Vorgangsnummer der Kasse (nextFree − 1, §7.1) */
  lastBonId: z.int().nonnegative(),
  /** Soll-Bestand der Schicht laut Server, in derselben Transaktion wie lastBonId (§11.6) */
  expectedCashCents: z.int(),
  /** aktuelle Version des Stammdaten-Pakets */
  snapshotVersion: zHex64,
  /** Server-Nonce für Geräte-Nachweise; gilt bis zum übernächsten Herzschlag */
  nonce: zNonce,
  nonceUntil: zIsoDateTime,
  /** Vordruck-Reserve des Geräts (§11.5): ausgegebene Reihen und davon schon gemeldete Blattnummern */
  paper: z.object({ ranges: z.array(z.object({ from: z.int().positive(), to: z.int().positive() })), used: z.array(z.int().positive()) }),
})
export type StartpackState = z.output<typeof zStartpackState>

export const zStartpack = z.object({ rights: zStartpackRights, state: zStartpackState })
export type Startpack = z.output<typeof zStartpack>

// ---- Stammdaten-Paket (§5.1) ---------------------------------------------------------------------------------------

const zSnapshotItem = z.object({
  id: zId,
  sku: z.string(),
  name: z.string(),
  kind: z.string(),
  taxCodeId: zId,
  serialTracked: z.boolean(),
  basePriceRequired: z.boolean(),
  contentQty: z.int().nullable(),
  contentUnitCode: z.string().nullable(),
  active: z.boolean(),
  /** Mindestalter (0, 16, 18) */
  minAge: z.int().min(0).max(99),
  groupId: zId.nullable(),
  groupCode: z.string().nullable(),
  groupName: z.string().nullable(),
})

const zSnapshotUnit = z.object({
  id: zId,
  itemId: zId,
  baseQty: z.int().positive(),
  code: z.string(),
  short: z.string(),
  uneceCode: z.string().nullable(),
  label: z.string().nullable(),
  depositTypeId: zId.nullable(),
  isBase: z.boolean(),
  /** Standardgebinde an der Kasse (isDefault in §5.1) */
  isSalesDefault: z.boolean(),
  isPurchaseDefault: z.boolean(),
  /** Nachkommastellen der Menge im Gebinde (0 Stückware … 3 lose Ware) */
  decimals: z.int().min(0).max(3),
  barcodes: z.array(z.string().max(80)),
  /** erster Barcode mit 8–14 Ziffern */
  gtin: z.string().regex(/^\d{8,14}$/).nullable(),
})

const zSnapshotTaxCode = z.object({
  id: zId,
  key: z.string(),
  name: z.string(),
  category: z.string(),
  exemptionReason: z.string().nullable(),
  /** Nullsatz nach § 12 Abs. 3 (USt-Schlüssel 8 statt 6) */
  zeroRated: z.boolean(),
  rates: z.array(z.object({ from: zIsoDay, to: zIsoDay.nullable(), rateBp: z.int().min(0).max(10_000) })),
})

const zSnapshotDeposit = z.object({
  id: zId,
  key: z.string(),
  name: z.string(),
  shortName: z.string().nullable(),
  kind: z.enum(['single_use_dpg', 'reusable', 'transport_aid', 'rental_container']),
  amountBasis: z.enum(['gross', 'net']),
  taxMode: z.enum(['inherit', 'fixed']),
  fallbackTaxCodeId: zId.nullable(),
  isComposite: z.boolean(),
  emptiesItemId: zId.nullable(),
  components: z.array(z.object({ depositTypeId: zId, qty: z.int().positive() })),
  rates: z.array(z.object({ from: zIsoDay, amountCents: z.int() })),
})

const zSnapshotPriceList = z.object({
  id: zId,
  name: z.string(),
  priceGroupId: zId.nullable(),
  priceGroupName: z.string().nullable().optional(),
  /** nur Listen ohne Kunde */
  partnerId: z.null(),
  locationId: zId.nullable(),
  validFrom: zIsoDay.nullable(),
  validTo: zIsoDay.nullable(),
  priority: z.int(),
  isPromotion: z.boolean(),
  priceMode: z.enum(['gross', 'net']),
})

const zSnapshotPrice = z.object({
  id: zId,
  priceListId: zId,
  itemUnitId: zId,
  minQty: z.int().nonnegative(),
  amountCents: z.int(),
  pricePerQty: z.int().positive(),
  validFrom: zIsoDay,
  validTo: zIsoDay.nullable(),
})

/**
 * Stammdaten-Paket je Standort, Format SNAPSHOT_FORMAT. Gebaut mit denselben Ladern wie der Belegkern
 * (`documents.composeDataForLocation`) für Laufkundschaft an diesem Standort. Ohne Kunden, Kundenpreise,
 * Einstandspreise, Bestand, Seriennummern, Gutscheine.
 *
 * `version` = hex(SHA-256(canonicalJson(snapshotHashBasis(paket)))) – der Prüfwert über den INHALT ohne `version` und
 * `createdAt`; so erkennt der Herzschlag auch gelöschte Barcodes, Gebinde, künftige Preise und Steuersätze.
 * Das Paket ist zugleich `ComposeData` für composeDocument (shared/documentCompose.ts) – Typprüfung unten.
 */
export const zOfflineSnapshot = z.object({
  format: z.literal(SNAPSHOT_FORMAT),
  version: zHex64,
  tenantId: zId,
  locationId: zId,
  createdAt: zIsoDateTime,
  /** heute und morgen (Europe/Berlin); das Gerät rechnet nur, wenn businessTo den Geschäftstag umfasst */
  businessFrom: zIsoDay,
  businessTo: zIsoDay,
  priceMode: z.literal('gross'),
  basePriceGroupId: zId.nullable(),
  items: z.array(zSnapshotItem),
  units: z.array(zSnapshotUnit).max(SNAPSHOT_MAX_UNITS),
  taxCodes: z.array(zSnapshotTaxCode),
  deposits: z.array(zSnapshotDeposit),
  priceLists: z.array(zSnapshotPriceList),
  prices: z.array(zSnapshotPrice),
})
export type OfflineSnapshot = z.output<typeof zOfflineSnapshot>
export type SnapshotItem = OfflineSnapshot['items'][number]
export type SnapshotUnit = OfflineSnapshot['units'][number]

/** Inhalt, über den die Version gerechnet wird: das Paket ohne `version` und `createdAt`. */
export function snapshotHashBasis(snapshot: OfflineSnapshot): Omit<OfflineSnapshot, 'version' | 'createdAt'> {
  const { version: _version, createdAt: _createdAt, ...content } = snapshot
  return content
}

/** Das Stammdaten-Paket ist ComposeData (dieselbe Rechenfunktion auf Server und Gerät, O8). */
export const composeDataOf = (snapshot: OfflineSnapshot): ComposeData => snapshot

// ---- Geräte-Nachweis und Echo (§14.1) ------------------------------------------------------------------------------

/**
 * Geräte-Nachweis jeder vorgangsbildenden Aktion einer Offline-Kasse, der Reservierung und der Übertragung (§4.1 Nr. 3).
 * Signiert wird canonicalJson(proofMessage(action, registerId, proof)) (format.ts) mit dem Geräteschlüssel.
 */
export const zTillProof = z.strictObject({
  deviceId: zId,
  /** Nummernvorschlag (Aktionen, die Vorgänge anlegen) */
  nextBonId: z.int().positive().optional(),
  /** erster Bonschritt: Vorgangs-ID des Geräts */
  vorgangId: zId.optional(),
  /** aus Herzschlag bzw. Startpaket; Pflicht außer beim Herzschlag */
  nonce: z.string().max(64).optional(),
  /** geschätzte Serverzeit (§9.5); ± PROOF_WINDOW_MS zur Serverzeit (Herzschlag: nicht älter als der letzte) */
  at: zIsoDateTime,
  /** hex(SHA-256(canonicalJson(Eingabe ohne till))) */
  inputHash: zHex64,
  sig: zSignature,
})
export type TillProof = z.output<typeof zTillProof>

/** Antwort der Kassenaufrufe an einer Offline-Kasse: höchste vergebene bzw. reservierte Vorgangsnummer der Kasse. */
export const zTillEcho = z.object({ lastBonId: z.int().nonnegative() }).nullable()
export type TillEcho = z.output<typeof zTillEcho>

/** Offline-Angaben einer Kasse in pos.till.state (§14.2). */
export const zTillOffline = z.object({
  /** Betrieb hat Offline-Betrieb eingeschaltet (mit Freigabe) */
  enabled: z.boolean(),
  /** diese Kasse verkauft auf diesem Gerät ohne Verbindung */
  allowed: z.boolean(),
  /** warum nicht – fertiger deutscher Satz */
  reason: z.string().nullable(),
  device: z
    .object({
      id: zId,
      name: z.string(),
      state: zDeviceState,
      lastSeenAt: z.date().nullable(),
      persisted: z.boolean().nullable(),
      displayMode: z.enum(DISPLAY_MODES).nullable(),
    })
    .nullable(),
  settings: zOfflineLimits,
  /** nächste freie Vorgangsnummer der Kasse (nach Reservierungen) */
  nextBonId: z.int().positive(),
})
export type TillOffline = z.output<typeof zTillOffline>


// ---- Gerät: Zeilen des Speichers, die mehrere Pakete lesen (§9.1) ----------------------------------------------------

/** Satz in IndexedDB `entries` (Schlüssel deviceSeq): nur anfügen, danach ändert sich nur state/result. */
export interface DeviceEntryRow {
  seq: number
  kind: string
  /** kanonisches JSON genau wie signiert */
  payload: string
  hash: string
  sig: string
  state: DeviceEntryState
  /** Antwort des Servers (accepted/duplicate/quarantined mit Klärfällen) */
  result?: { result: SubmitResult; entryId: string; bonId: number | null; receiptNumber: string | null; at: string }
  createdAt: string
  bonId?: number
}
